import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import type { ProviderCode } from '@patrianna/shared-patrianna-types/store/ProviderModule'
import { getTranslationFromJson } from '@patrianna/shared-utils/prismic'
import cx from 'classnames'
import { useEffect, useState } from 'react'

import OrientationContainer from 'components/OrientationContainer'
import VolatilityContainer from 'components/VolatilityContainer'
import content from 'prismic-translates/start-game-dialog.json'
import { useThemeContext } from 'theme/themeProvider'
import type { SupportedCurrencies } from 'types/currencies'

import GameType from '../GameType'
import Provider from '../Provider'

import PrizeInfo from './PrizeInfo'
import classes from './styles.module.scss'
import { getSlotBetsAndPrizes, getGameTypeData, getGridOptions } from './utils'

type Props = {
  isLoggedIn: boolean
  gameProduct: GameProduct
  activeCurrency: SupportedCurrencies
  providerCodes: ProviderCode[]
}

export default function GameInfo(props: Props) {
  const { activeCurrency, gameProduct, isLoggedIn, providerCodes } = props
  const [gridOptions, setGridOptions] = useState({ isOdd: true, isSmall: true })
  const { isDarkMode } = useThemeContext()
  const descriptionColor = isLoggedIn ? classes[activeCurrency as keyof typeof classes] : classes['defaultColor']
  const { gameType, categoryLink } = getGameTypeData(gameProduct)
  const t = getTranslationFromJson([content])

  useEffect(() => {
    const gridLength = document.getElementsByClassName(classes.gameInfoCol).length
    setGridOptions(getGridOptions(gridLength))
  }, [])

  return (
    <div className={cx(classes.root)}>
      <div
        className={cx(
          classes.prizeDescription,
          gridOptions.isOdd && classes.oddGrid,
          gridOptions.isSmall && classes.smallGrid,
        )}
      >
        {Boolean(gameProduct) && gameProduct.type !== 'scratch_card' && (
          <GameType
            isDarkMode={isDarkMode}
            activeCurrency={activeCurrency}
            isLoggedIn={isLoggedIn}
            descriptionColor={descriptionColor}
            gameType={gameType}
            categoryLink={categoryLink}
            typeTitle={t('type_title')}
          />
        )}

        {Boolean(gameProduct) && (
          <PrizeInfo
            isDarkMode={isDarkMode}
            activeCurrency={activeCurrency}
            gameBetsAndPrizes={getSlotBetsAndPrizes(gameProduct, activeCurrency)}
            descriptionColor={descriptionColor}
            isLoggedIn={isLoggedIn}
          />
        )}

        {Boolean(gameProduct?.volatility) && (
          <div className={cx(classes.gameInfoCol, isDarkMode && classes.darkMode)}>
            <VolatilityContainer
              volatilityValue={gameProduct.volatility}
              activeCurrency={activeCurrency}
              isLoggedIn={isLoggedIn}
              isDarkMode={isDarkMode}
              title={t('volatility')}
              captionClassName='gameLandingCaption'
              isGameDescription
            />
          </div>
        )}

        {Boolean(gameProduct?.orientation) && (
          <div className={cx(classes.gameInfoCol, isDarkMode && classes.darkMode)}>
            <OrientationContainer
              activeCurrency={activeCurrency}
              orientation={gameProduct.orientation}
              isLoggedIn={isLoggedIn}
              isDarkMode={isDarkMode}
              title={t('orientation')}
              captionClassName='gameLandingCaption'
            />
          </div>
        )}

        {Boolean(gameProduct?.supplierSearchCode) &&
          providerCodes?.includes(gameProduct?.supplierSearchCode as ProviderCode) && (
            <Provider
              isDarkMode={isDarkMode}
              activeCurrency={activeCurrency}
              isLoggedIn={isLoggedIn}
              providerCode={gameProduct.supplierSearchCode}
              title={t('provider_title')}
            />
          )}
      </div>
    </div>
  )
}
